import angular from 'angular';
import '../../layouts.module';
import template from './login.component.html';
import './login.component.scss';
import logoUrl from '../../../assets/images/whatsapp.png';
import { homeState } from '../../../core/constants/menu.constant';

const SLIDES = [
  {
    icon: 'message-circle',
    title: 'Atenda no WhatsApp como nunca antes',
    description:
      'Centralize conversas, contatos e vendas em um único painel, com a agilidade que seu time merece.'
  },
  {
    icon: 'zap',
    title: 'Automatize sem perder o toque humano',
    description:
      'Chatbots, respostas rápidas e automações que trabalham por você, 24 horas por dia.'
  },
  {
    icon: 'trending-up',
    title: 'Venda mais com dados em tempo real',
    description: 'Dashboards completos de conversas, funil de vendas e desempenho da equipe.'
  },
  {
    icon: 'shield',
    title: 'Segurança e controle em cada etapa',
    description:
      'Permissões por perfil, auditoria completa e integrações com os principais provedores de WhatsApp.'
  }
];

const STATS = [
  { value: '+50 mil', label: 'mensagens processadas por dia' },
  { value: '98%', label: 'satisfação dos clientes' },
  { value: '24/7', label: 'suporte especializado' }
];

const SLIDE_INTERVAL_MS = 4500;

class LoginController {
  constructor(AuthService, NotificationService, SessionService, $state, $interval) {
    this.AuthService = AuthService;
    this.SessionService = SessionService;
    this.NotificationService = NotificationService;
    this.$state = $state;
    this.$interval = $interval;
    this.email = '';
    this.password = '';
    this.tenantSlug = '';
    this.tenantRequired = false;
    this.loading = false;
    this.captcha = { status: 'loading', siteKey: null, token: null, api: null };
    this.logoUrl = logoUrl;
    this.slides = SLIDES;
    this.stats = STATS;
    this.activeSlideIndex = 0;
    this.step = 'login';
    this.currentPassword = '';
    this.askCurrentPassword = false;
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = '';
  }

  $onInit() {
    const user = this.SessionService.user;
    if (this.SessionService.isAuthenticated() && user && user.mustChangePassword) {
      this.step = 'password';
      this.askCurrentPassword = true;
      this.email = user.email;
    }
    this.loadCaptchaSettings();
    this.slideTimer = this.$interval(() => {
      this.activeSlideIndex = (this.activeSlideIndex + 1) % this.slides.length;
    }, SLIDE_INTERVAL_MS);
  }

  $onDestroy() {
    this.$interval.cancel(this.slideTimer);
  }

  selectSlide(index) {
    this.activeSlideIndex = index;
  }

  loadCaptchaSettings() {
    this.captcha = { ...this.captcha, status: 'loading' };
    return this.AuthService.captchaSettings().then(
      (settings) => {
        this.captcha = {
          ...this.captcha,
          status: settings.enabled ? 'required' : 'disabled',
          siteKey: settings.siteKey
        };
      },
      () => {
        this.captcha = { ...this.captcha, status: 'unavailable' };
      }
    );
  }

  captchaReady(api) {
    this.captcha.api = api;
  }

  captchaSolved(token) {
    this.captcha.token = token;
  }

  get canSubmit() {
    if (this.loading) return false;
    if (this.captcha.status === 'disabled') return true;
    return this.captcha.status === 'required' && Boolean(this.captcha.token);
  }

  submit() {
    if (!this.canSubmit) return null;
    this.loading = true;
    return this.AuthService.login(this.email, this.password, this.tenantSlug, this.captcha.token)
      .then((user) => {
        if (user && user.mustChangePassword) {
          this.currentPassword = this.password;
          this.step = 'password';
          return;
        }
        this.NotificationService.success('Bem-vindo(a) de volta!');
        this.goHome();
      })
      .catch((error) => {
        const message = error?.data?.message;
        if (error?.status === 409 && error?.data?.error === 'TenantRequired')
          this.tenantRequired = true;
        this.NotificationService.error(
          Array.isArray(message)
            ? message.join(', ')
            : message || 'Não foi possível entrar. Verifique suas credenciais.'
        );
        this.captcha.token = null;
        if (this.captcha.api) this.captcha.api.reset();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  goHome() {
    this.$state.go(homeState(this.SessionService) || 'app.dashboard');
  }

  validateNewPassword() {
    if (!this.currentPassword) return 'Informe a senha atual.';
    if (this.newPassword.length < 8) return 'A nova senha deve ter ao menos 8 caracteres.';
    if (!/[0-9]/.test(this.newPassword) || !/[a-zA-Z]/.test(this.newPassword))
      return 'A nova senha deve conter letras e números.';
    if (this.newPassword === this.currentPassword)
      return 'A nova senha deve ser diferente da senha atual.';
    if (this.newPassword !== this.confirmPassword) return 'As senhas não conferem.';
    return '';
  }

  submitNewPassword() {
    if (this.loading) return null;
    this.passwordError = this.validateNewPassword();
    if (this.passwordError) return null;
    this.loading = true;
    return this.AuthService.changePassword(this.currentPassword, this.newPassword)
      .then(() => {
        this.SessionService.clearSession();
        this.step = 'login';
        this.password = '';
        this.currentPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
        this.captcha.token = null;
        if (this.captcha.api) this.captcha.api.reset();
        this.NotificationService.success('Senha alterada. Entre com a nova senha.');
      })
      .catch((error) => {
        const message = error?.data?.message;
        this.passwordError = Array.isArray(message)
          ? message.join(', ')
          : message || 'Não foi possível alterar a senha.';
      })
      .finally(() => {
        this.loading = false;
      });
  }

  cancelNewPassword() {
    return this.AuthService.logout().finally(() => {
      this.step = 'login';
      this.password = '';
      this.currentPassword = '';
      this.newPassword = '';
      this.confirmPassword = '';
      this.passwordError = '';
    });
  }

  useGeneratedPassword(password) {
    this.newPassword = password;
    this.confirmPassword = password;
    this.passwordError = '';
  }
}

LoginController.$inject = [
  'AuthService',
  'NotificationService',
  'SessionService',
  '$state',
  '$interval'
];

angular.module('app.layouts').component('cpLogin', {
  controller: LoginController,
  controllerAs: 'vm',
  template
});
